import React from 'react';
import { SectionContainer } from '../components/SectionContainer';
import SectionTitle from '../components/SectionTitle';
import { SectionBody } from '../components/SectionBody';
import { SectionLine } from '../components/SectionLine';
import { FormikPremiumInput } from 'refactored/features/emergencies/ui/inputs/customInput';
import {
  FieldErrorMessageContainer,
  FieldErrorMessageText,
} from 'refactored/features/emergencies/ui/fieldErrorsMessage';
import usePostalCodeFilter from '../use-postal-code-filter';
import { withTranslation } from 'react-i18next';

const countryOptions = [
  {
    value: 'ES',
    label: 'Spain',
  },
  {
    value: 'AD',
    label: 'Andorra',
  },
  {
    value: 'AT',
    label: 'Austria',
  },
  {
    value: 'BE',
    label: 'Belgium',
  },
  {
    value: 'BG',
    label: 'Bulgaria',
  },
  {
    value: 'CH',
    label: 'Switzerland',
  },
  {
    value: 'CZ',
    label: 'Czechia',
  },
  {
    value: 'DE',
    label: 'Germany',
  },
  {
    value: 'DK',
    label: 'Denmark',
  },
  {
    value: 'DZ',
    label: 'Algeria',
  },
  {
    value: 'EE',
    label: 'Estonia',
  },
  {
    value: 'FI',
    label: 'Finland',
  },
  {
    value: 'FR',
    label: 'France',
  },
  {
    value: 'GB',
    label: 'UK',
  },
  {
    value: 'GR',
    label: 'Greece',
  },
  {
    value: 'HR',
    label: 'Croatia',
  },
  {
    value: 'HU',
    label: 'Hungary',
  },
  {
    value: 'IE',
    label: 'Ireland',
  },
  {
    value: 'IT',
    label: 'Italy',
  },
  {
    value: 'LI',
    label: 'Liechtenstein',
  },
  {
    value: 'LT',
    label: 'Lithuania',
  },
  {
    value: 'LU',
    label: 'Luxembourg',
  },
  {
    value: 'MA',
    label: 'Morocco',
  },
  {
    value: 'ME',
    label: 'Montenegro',
  },
  {
    value: 'MT',
    label: 'Malta',
  },
  {
    value: 'NL',
    label: 'Netherlands',
  },
  {
    value: 'NO',
    label: 'Norway',
  },
  {
    value: 'PL',
    label: 'Poland',
  },
  {
    value: 'PT',
    label: 'Portugal',
  },
  {
    value: 'RO',
    label: 'Romania',
  },
  {
    value: 'RS',
    label: 'Serbia',
  },
  {
    value: 'SE',
    label: 'Sweden',
  },
  {
    value: 'SI',
    label: 'Slovenia',
  },
  {
    value: 'SK',
    label: 'Slovakia',
  },
  {
    value: 'TN',
    label: 'Tunisia',
  },
  {
    value: 'TR',
    label: 'Turkey',
  },
].map((country) => ({
  search: country.label,
  display: country.label,
  ...country,
}));

export const DropoffSection = withTranslation('Emergencies')(
  ({ ticketFormBodyData, t }) => {
    const {
      showErrors,
      isEdit,
      isReadOnly,
      isDraft,
      backendErrors,
      values,
      draftErrors,
      initialValues,
    } = ticketFormBodyData;

    const translatedCountryOptions = countryOptions
      .map((country) => ({
        ...country,
        display: t(`countries.${country.label}`),
        search: t(`countries.${country.label}`),
        label: t(`countries.${country.label}`),
      }))
      .sort((a, b) => a.display.localeCompare(b.display));

    const initialPostalCodeQuery = values?.order_data?.dropoff?.zipcode || '';

    const zipcodeUnchanged =
      values.order_data.dropoff.zipcode ===
      initialValues.order_data.dropoff.zipcode;

    const {
      zipcodeOptions,
      isLoadingZipcodesOptions,
      zipcodeOptionsError,
      setSearch,
      setCountry,
      setZipcode,
    } = usePostalCodeFilter({
      stop: 'dropoff',
      initialQuery: initialPostalCodeQuery,
      country: values.order_data.dropoff.country,
    });

    const hasInvalidZipcode = !!draftErrors?.order_data?.dropoff?.zipcode;

    const showZipCodeBackendError =
      JSON.stringify(backendErrors).includes('dropoff.zipcode');
    return (
      <SectionContainer>
        <SectionTitle>{t('dropoff')}</SectionTitle>
        <SectionBody>
          <SectionLine></SectionLine>
          <SectionLine>
            <FormikPremiumInput
              label={t('company_name')}
              name="order_data.dropoff.company_name"
              showErrors={showErrors}
              isDisabled={isEdit || isReadOnly}
            />
          </SectionLine>
          <SectionLine>
            <FormikPremiumInput
              label={t('address')}
              showErrors={showErrors}
              name="order_data.dropoff.address"
              isRequired={true}
              isDisabled={isEdit || isReadOnly}
            />
          </SectionLine>
          <SectionLine>
            <FormikPremiumInput
              isRequired={true}
              label={t('city')}
              showErrors={showErrors}
              name="order_data.dropoff.city"
              isDisabled={isEdit || isReadOnly}
            />

            <FormikPremiumInput
              type="select-with-search"
              onFieldChange={setCountry}
              label={t('country')}
              searchLabel={t('search_country')}
              showErrors={showErrors}
              optionsEmptyMssg={t('no_country_found')}
              isClereable={true}
              isDisabled={isEdit || isReadOnly}
              isRequired={true}
              name="order_data.dropoff.country"
              options={translatedCountryOptions}
            />
            <FormikPremiumInput
              type="select-with-search"
              onSearchChange={setSearch}
              onFieldChange={setZipcode}
              options={zipcodeOptions}
              isLoadingOptions={isLoadingZipcodesOptions}
              label={t('zipcode_label')}
              searchLabel={t('zipcode_search_label')}
              showErrors={showErrors}
              customHasError={
                showZipCodeBackendError ||
                zipcodeOptionsError ||
                (zipcodeUnchanged && hasInvalidZipcode)
              }
              optionsEmptyMssg={t('no_zipcode')}
              isClereable={true}
              isDisabled={(isEdit || isReadOnly) && !isDraft}
              isRequired={true}
              initialValue={initialPostalCodeQuery}
              name="order_data.dropoff.zipcode"
            />
          </SectionLine>
          {showZipCodeBackendError && (
            <SectionLine>
              <FieldErrorMessageContainer>
                <FieldErrorMessageText>
                  {t('zipcode_backend_error')}
                </FieldErrorMessageText>
              </FieldErrorMessageContainer>
            </SectionLine>
          )}
          <SectionLine>
            <FormikPremiumInput
              label={t('date')}
              showErrors={showErrors}
              name="order_data.dropoff.date"
              type="date"
              isRequired={true}
              isDisabled={isEdit || isReadOnly}
            />
            <FormikPremiumInput
              label={t('start_time')}
              isRequired={true}
              showErrors={showErrors}
              name="order_data.dropoff.time_from"
              type="time"
              isDisabled={isEdit || isReadOnly}
            />
            <FormikPremiumInput
              label={t('end_time')}
              showErrors={showErrors}
              name="order_data.dropoff.time_to"
              type="time"
              isRequired={true}
              isDisabled={isEdit || isReadOnly}
            />
          </SectionLine>
          <SectionLine>
            <FormikPremiumInput
              label={t('observations')}
              isDisabled={isReadOnly}
              name="order_data.dropoff.observations"
            />
          </SectionLine>
        </SectionBody>
      </SectionContainer>
    );
  }
);
